<script lang="ts" setup>
import {
    faCalendar,
    faCalendarClock,
    faClock,
    faFingerprint,
    faImage,
    faInputNumeric,
    faInputText,
    faLink,
    faMailbox,
    faMobile,
    faPalette,
    faText,
} from "@fortawesome/pro-light-svg-icons";
import { object, string } from "yup";
import type { FieldDefinition } from "~/models/Automation";

const STATE = {
    key: "",
    type: "string",
    sub_type: null,
    label: "",
    default_value: "",
};

const props = withDefaults(
    defineProps<{
        field?: FieldDefinition;
        canChooseType?: boolean;
        fields?: FieldDefinition[];
    }>(),
    {
        fields: () => [],
    },
);

const state = ref<FieldDefinition>({
    ...STATE,
    ...(useCloneDeep(props.field ?? {}) as FieldDefinition),
});

watch(
    () => props.field,
    (to) =>
        (state.value = to
            ? (useCloneDeep(to) as FieldDefinition)
            : (useCloneDeep(STATE) as FieldDefinition)),
);

const type = computed({
    get: () =>
        state.value.sub_type
            ? `${state.value.type}|${state.value.sub_type}`
            : (state.value.type as string),
    set: (type: string) => {
        const split = type?.split("|") ?? ["text"];

        state.value.type = split[0] as FieldDefinition["type"];
        state.value.sub_type =
            (split[1] as FieldDefinition["sub_type"]) ?? null;
        state.value.default_value = null;
        progress.value = 1;
    },
});

const emit = defineEmits<{
    (e: "saved", field: FieldDefinition): void;
    (e: "cancelled"): void;
    (e: "update:field", field: FieldDefinition): void;
}>();

const progress = ref<number>(props.canChooseType ? 0 : 1);
const steps = computed(() => [
    {
        label: "Step 1",
        title: "Choose Field Type",
        key: 0,
        onNext: () => false,
        onPrevious: () => props.canChooseType,
    },
    {
        label: "Step 2",
        title: "Configure your field",
        key: 1,
        onNext: () => false,
        onPrevious: () => false,
    },
]);

const types = computed(() => {
    return [
        { label: "Id", value: "string|id", icon: faFingerprint },
        { label: "Text", value: "string", icon: faInputText },
        { label: "Wysiwyg", value: "string|wysiwyg", icon: faText },
        { label: "Number", value: "number", icon: faInputNumeric },
        { label: "Datetime", value: "datetime", icon: faCalendarClock },
        { label: "Date", value: "date", icon: faCalendar },
        { label: "Time", value: "time", icon: faClock },
        { label: "Email", value: "string|email", icon: faMailbox },
        { label: "Phone", value: "string|phone", icon: faMobile },
        { label: "Color", value: "string|color", icon: faPalette },
        { label: "Url", value: "string|url", icon: faLink },
        { label: "Image", value: "string|image", icon: faImage },
    ];
});

const selectedType = computed(() =>
    types.value.find(
        (type) =>
            type.value ===
            (state.value.sub_type
                ? `${state.value.type}|${state.value.sub_type}`
                : state.value.type),
    ),
);

const form = ref();

async function submit() {
    try {
        form.value!.validate().then(() => {
            emit("update:field", useCloneDeep(state.value) as FieldDefinition);
            emit("saved", useCloneDeep(state.value) as FieldDefinition);
        });
    } catch (e) {
        emit("cancelled");
    }
}

const schema = computed(() =>
    object({
        label: string().required().max(255),
        key: string()
            .required()
            .notOneOf(
                props.fields
                    .filter((item) => item.key !== props.field?.key)
                    .map((field) => field.key),
                "existing key cannot be used for key value.",
            )
            .matches(
                /^[a-zA-Z0-9_.]*$/,
                () =>
                    `Key must be an alphanumeric string with dots and underscores.`,
            )
            .max(255),
    }),
);
</script>

<template>
    <UiSteps
        v-model:active="progress"
        :disable-completed="!canChooseType"
        :steps="steps"
        class="mb-2 w-96"
    />
    <UForm
        ref="form"
        :schema="schema"
        :state="state"
        :validate-on="['submit']"
        class="flex flex-col gap-2 w-96 whitespace-normal"
        @submit="submit"
    >
        <div v-if="!progress" class="grid grid-cols-3 gap-2">
            <UButton
                v-for="option in types"
                :key="option.value"
                :padded="false"
                class="justify-center flex-col py-3 gap-1"
                color="white"
                @click="type = option.value"
            >
                <div class="h-8 flex items-center justify-center">
                    <FontAwesomeIcon :icon="option.icon" size="xl" />
                </div>
                <span class="capitalize text-sm">{{ option.label }}</span>
            </UButton>
            <UButton
                class="justify-center col-span-full"
                color="white"
                @click="emit('cancelled')"
                >Cancel
            </UButton>
        </div>
        <template v-if="progress === 1">
            <div
                v-if="selectedType"
                class="flex text-sm gap-2 items-center border rounded-md border-gray-300 bg-hd-neutral-50 px-3 py-2"
            >
                <FontAwesomeIcon :icon="selectedType.icon" />
                <span>{{ selectedType.label }}</span>
            </div>
            <UFormGroup label="Label" name="label">
                <UInput v-model="state.label" name="label" />
            </UFormGroup>
            <UFormGroup label="Key" name="key">
                <UInput v-model="state.key" name="key" />
            </UFormGroup>
            <CreateFieldDefinitionValue
                v-model="state"
                help="Default value will be used as a fallback value when sending the email and no value for this field was provided."
            />
            <div class="mt-2 grid grid-cols-2 gap-4">
                <UButton
                    class="justify-center"
                    color="white"
                    @click="emit('cancelled')"
                    >Cancel
                </UButton>
                <UButton class="justify-center" type="submit">Save</UButton>
            </div>
        </template>
    </UForm>
</template>
